import ApiWithLogging from '../utils/apiWithLogging';

// Types
export interface DeviceModel {
  id: number;
  code: string;
  name: string;
  brand_id: number;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  created_by_id?: number;
  updated_by_id?: number;
  created_by?: {
    id: number;
    username: string;
    full_name?: string;
  };
  updated_by?: {
    id: number;
    username: string;
    full_name?: string;
  };
  brand?: {
    id: number;
    code: string;
    name: string;
  };
}

export interface CreateDeviceModelRequest {
  code: string;
  name: string;
  brand_id: number;
  description?: string;
  is_active?: boolean;
}

export interface UpdateDeviceModelRequest {
  code?: string;
  name?: string;
  brand_id?: number;
  description?: string;
  is_active?: boolean;
}

export interface GetDeviceModelsParams {
  page?: number;
  limit?: number;
  search?: string;
  brand_id?: number;
  is_active?: boolean;
}

export interface DeviceModelListResponse {
  items: DeviceModel[];
  total: number;
  page: number;
  limit: number;
}

const deviceModelService = {
  // ดึงรายการรุ่นอุปกรณ์ทั้งหมด
  async getAll(params?: GetDeviceModelsParams): Promise<DeviceModelListResponse> {
    const queryParams: Record<string, string | number | boolean> = {};

    if (params?.page) queryParams.page = params.page;
    if (params?.limit) queryParams.limit = params.limit;
    if (params?.search) queryParams.search = params.search;
    if (params?.brand_id) queryParams.brand_id = params.brand_id;
    if (params?.is_active !== undefined) queryParams.is_active = params.is_active;

    // ApiWithLogging.get already returns response.data, no need to access .data again
    return await ApiWithLogging.get<DeviceModelListResponse>('/device-models/', { params: queryParams });
  },

  // ดึงรายการรุ่นอุปกรณ์ที่ active
  async getActive(): Promise<DeviceModel[]> {
    // ApiWithLogging already returns response.data directly
    return await ApiWithLogging.get<DeviceModel[]>('/device-models/active');
  },

  // ดึงรายการรุ่นอุปกรณ์ตามยี่ห้อ
  async getByBrand(brandId: number, activeOnly: boolean = true): Promise<DeviceModel[]> {
    return await ApiWithLogging.get<DeviceModel[]>(`/device-models/by-brand/${brandId}`, {
      params: { active_only: activeOnly }
    });
  },

  // ดึงข้อมูลรุ่นอุปกรณ์ตาม ID
  async getById(id: number): Promise<DeviceModel> {
    return await ApiWithLogging.get<DeviceModel>(`/device-models/${id}`);
  },

  // สร้างรุ่นอุปกรณ์ใหม่
  async create(data: CreateDeviceModelRequest): Promise<DeviceModel> {
    return await ApiWithLogging.post<DeviceModel>('/device-models/', data);
  },

  // อัพเดทรุ่นอุปกรณ์
  // BUG-S162-P1: pass last-seen updated_at as `ifMatch` to enable backend optimistic lock (stale→409). Optional.
  async update(id: number, data: UpdateDeviceModelRequest, ifMatch?: string): Promise<DeviceModel> {
    return await ApiWithLogging.put<DeviceModel>(`/device-models/${id}`, data, ifMatch ? { headers: { 'If-Match': ifMatch } } : {});
  },

  // ลบรุ่นอุปกรณ์
  async delete(id: number): Promise<DeviceModel> {
    return await ApiWithLogging.delete<DeviceModel>(`/device-models/${id}`);
  },

  // เปลี่ยนสถานะ active/inactive (สลับอัตโนมัติ)
  async toggleStatus(id: number): Promise<DeviceModel> {
    return await ApiWithLogging.patch<DeviceModel>(`/device-models/${id}/toggle`);
  },

  // ส่งออกข้อมูลรุ่นอุปกรณ์เป็น Excel
  // ส่งออก Excel — ต้องส่งตัวกรองที่หน้าจอใช้อยู่ไปด้วย ไม่งั้นผู้ใช้ค้นหา/กรองแล้วกดส่งออก
  // จะได้ไฟล์ "ทั้งระบบ" โดยไม่มีอะไรเตือน (GAP-EXPORT-FILTER)
  // ชุดพารามิเตอร์ต้องตรงกับที่ list query ส่ง และตรงกับ signature ของ endpoint
  async exportToExcel(params?: { search?: string; brand_id?: number | null; is_active?: boolean }): Promise<Blob> {
    const query: Record<string, any> = {};
    if (params?.search) query.search = params.search;
    if (params?.brand_id !== undefined && params?.brand_id !== null) query.brand_id = params.brand_id;
    if (params?.is_active !== undefined) query.is_active = params.is_active;

    const response = await ApiWithLogging.get('/device-models/export/excel',
      {
        responseType: 'blob',
        params: query
      },
      {
        service: 'deviceModelService',
        action: 'export_device_models_excel',
        entityType: 'device_model'
      }
    );
    return response;
  }
};

export default deviceModelService;
